import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_ASSET_BASE_URL } from '../../../apiConstant';
import TopActionBar from '../../../uikit/top_actionbar';
import Input from '../../../uikit/input';
import SimpleTable from '../../../uikit/simple_table';

export default function AssetUsers() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [searchString, setSearchStr] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const debounceTimerRef = useRef(null);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  // Sync state if search query param in URL changes externally
  useEffect(() => {
    const querySearch = searchParams.get('search') || '';
    setSearchStr(querySearch);
    setDebouncedSearch(querySearch);
  }, [searchParams]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchStr(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setPageIndex(0);
      setDebouncedSearch(value);
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (value.trim()) {
            newParams.set('search', value.trim());
          } else {
            newParams.delete('search');
          }
          return newParams;
        },
        { replace: true }
      );
    }, 400);
  };

  const { data: userData, isLoading } = useQuery({
    queryKey: ['userData', pageIndex, pageSize, debouncedSearch],
    queryFn: async () => {
      const res = await axios.get(`${API_ASSET_BASE_URL}/api/assets/assignments/users`, {
        params: {
          page: pageIndex,
          size: pageSize,
          search: debouncedSearch.trim() || undefined,
        },
      });
      return res.data?.data;
    },
  });

  const getStatusLabel = (status) => {
    if (!status) return '-';
    return status
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const onViewClick = (row) => {
    if (row?.original?.asset?.id) {
      navigate(`/asset-management/asset/${row.original.asset.id}/view`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'AVAILABLE':
        return 'bg-green-100 text-green-800';
      case 'PENDING_ACCEPTANCE':
        return 'bg-yellow-100 text-yellow-800';
      case 'IN_USE':
        return 'bg-blue-100 text-blue-800';
      case 'UNDER_MAINTENANCE':
        return 'bg-orange-100 text-orange-800';
      case 'RETURNED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
     id: 'slNo',
     header: 'Sl No',
      size: 40,
      minSize: 40,
      maxSize: 40,
     cell: (info) => info.row.index + 1 + (pageIndex * pageSize), // adjust if your table doesn't have pageIndex/pageSize in scope
   },
   {
     id: 'name',
     header: 'Name',
     accessorFn: (row) => row.user?.name,
     cell: (info) => info.getValue() || '-',
   },
    {
      id: 'roleName',
      header: 'Role',
      accessorFn: (row) => row.user?.roleName,
      cell: (info) => info.getValue() || '-',
    },
    {
      id: 'assetName',
      header: 'Asset Name',
      accessorFn: (row) => row.asset?.assetName,
      cell: ({ row, getValue }) => (
        <span
          className="font-medium text-blue-600 hover:underline cursor-pointer"
          onClick={() => onViewClick(row)}
          role="button"
        >
          {getValue() || '-'}
        </span>
      ),
    },
   {
     id: 'assetType',
     header: 'Asset Type',
     accessorFn: (row) => row.asset?.assetType?.name,
     cell: (info) => info.getValue() || '-',
   },
   {
     id: 'serialNumber',
     header: 'Serial Number',
     accessorFn: (row) => row.asset?.serialNumber,
     cell: (info) => info.getValue() || '-',
   },
   {
     id: 'status',
     header: 'Status',
    accessorFn: (row) => row.asset?.status,
    cell: (info) => {
      const status = info.getValue();
      if (!status) return '-';
      return (
        <span className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
          {getStatusLabel(status)}
        </span>
       );
     },
   },
 ];

  const data = userData?.content || [];
  const totalPages = userData?.totalPages || 1;

  return (
    <>
      <TopActionBar
        leftContent={
          <div className="flex items-center text-white">
            <h2 className="font-semibold text-lg">Asset Users</h2>
          </div>
        }
        rightContent={
          <div className="w-64 md:w-80">
            <Input
              type="text"
              placeholder="Search asset users..."
              value={searchString}
              onChange={handleSearchChange}
              parentClassName="mb-0"
            />
          </div>
        }
      />
      <div className="bg-white rounded-lg shadow flex flex-col">
        <SimpleTable
          data={data}
          columns={columns}
          showColumnSelector={false}
          loading={isLoading}
          isLast={userData?.last}
          isFirst={userData?.first}
          pageCount={totalPages}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPaginationChange={({ pageIndex: newPageIndex, pageSize: newPageSize }) => {
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
        />
      </div>
    </>
  );
}